// src/pages/AdminPage.jsx
import React, { useEffect, useState } from "react";
import {
  adminListNotices,
  adminCreateNotice,
  adminUpdateNotice,
  adminDeleteNotice,
  adminUploadNoticeFile,
} from "../lib/admin";

const ADMIN_KEY = "divingeasy_admin_auth";
const ADMIN_EXPIRE_HOURS = 1;
const ADMIN_PASSWORD = "tjrrudqja1010!";

export default function AdminPage() {
  const [auth, setAuth] = useState(false);
  const [password, setPassword] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState("");

  // 등록/수정 폼 상태
  const [editId, setEditId] = useState(null);
  const [type, setType] = useState("NOTICE");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [hasFile, setHasFile] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  // 로그인 체크
  useEffect(() => {
    const raw = localStorage.getItem(ADMIN_KEY);
    if (raw) {
      try {
        const { pw, ts } = JSON.parse(raw);
        const age = (Date.now() - ts) / (1000 * 60 * 60);
        if (pw === ADMIN_PASSWORD && age < ADMIN_EXPIRE_HOURS) {
          setAuth(true);
          return;
        }
      } catch {}
    }
  }, []);

  const onLogin = () => {
    if (password === ADMIN_PASSWORD) {
      localStorage.setItem(
        ADMIN_KEY,
        JSON.stringify({ pw: password, ts: Date.now() })
      );
      setAuth(true);
    } else {
      alert("비밀번호가 올바르지 않습니다.");
    }
  };

  const onLogout = () => {
    localStorage.removeItem(ADMIN_KEY);
    setAuth(false);
  };

  // 목록 로드
  const load = async () => {
    try {
      setLoading(true);
      setErr("");
      const data = await adminListNotices({ size: 1000 });
      const items = (Array.isArray(data) ? data : []).sort(
        (a, b) => (b.id ?? 0) - (a.id ?? 0)
      );
      setList(items);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth) load();
  }, [auth]);

  // 등록 또는 수정
  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      setToast("");
      let noticeId = editId;

      if (editId) {
        // 수정
        await adminUpdateNotice(editId, { type, title, content, hasFile });
        setToast("수정 완료!");
      } else {
        // 신규 등록
        const created = await adminCreateNotice({ type, title, content, hasFile });
        noticeId = created.id;
        setToast("등록 완료!");
      }

      // 새 파일 업로드
      if (selectedFiles.length > 0 && noticeId) {
        for (const file of selectedFiles) {
          await adminUploadNoticeFile(noticeId, file);
        }
      }

      setEditId(null);
      setTitle("");
      setContent("");
      setHasFile(false);
      setSelectedFiles([]);
      await load();
    } catch (e) {
      setToast(`등록/수정 실패: ${e.message || e}`);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(`정말 삭제할까요? (#${id})`)) return;
    try {
      await adminDeleteNotice(id);
      setToast("삭제 완료");
      await load();
    } catch (e) {
      setToast(`삭제 실패: ${e.message || e}`);
    }
  };

  const onEdit = async (row) => {
    setEditId(row.id);
    setType(row.type);
    setTitle(row.title);
    setContent(row.content || "");
    setHasFile(row.hasFile || false);
    setSelectedFiles([]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!auth) {
    return (
      <div style={{ maxWidth: 400, margin: "100px auto", textAlign: "center" }}>
        <h2>관리자 로그인</h2>
        <input
          type="password"
          placeholder="비밀번호 입력"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onLogin()}
          style={{ width: "100%", padding: "8px", marginBottom: "8px" }}
        />
        <button
          onClick={onLogin}
          style={{ marginTop: 10, width: "100%", padding: "8px" }}
        >
          로그인
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 960, margin: "40px auto", padding: "0 16px" }}>
      <h1>관리자: 공지 관리</h1>
      <button onClick={onLogout} style={{ marginBottom: "16px" }}>
        로그아웃
      </button>

      {toast && (
        <div
          style={{
            background: "#eef",
            padding: "8px 12px",
            margin: "12px 0",
            borderRadius: 8,
          }}
        >
          {toast}
        </div>
      )}
      {err && (
        <div
          style={{
            background: "#fee",
            padding: "8px 12px",
            margin: "12px 0",
            borderRadius: 8,
          }}
        >
          오류: {err}
        </div>
      )}

      <section
        style={{
          border: "1px solid #ddd",
          borderRadius: 12,
          padding: 16,
          marginBottom: 24,
        }}
      >
        <h2 style={{ marginTop: 0 }}>
          {editId ? `공지 수정 (#${editId})` : "새 공지 등록"}
        </h2>
        <form onSubmit={onSubmit} style={{ display: "grid", gap: 12 }}>
          <label>
            분류:
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              style={{ marginLeft: 8 }}
            >
              <option value="NOTICE">NOTICE</option>
              <option value="EVENT">EVENT</option>
            </select>
          </label>

          <label>
            제목:
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              style={{ width: "100%" }}
            />
          </label>

          <label>
            내용:
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={6}
              required
              style={{ width: "100%" }}
            />
          </label>

          <label>
            새 첨부파일 추가:
            <input
              type="file"
              multiple
              onChange={(e) => setSelectedFiles(Array.from(e.target.files || []))}
            />
            {selectedFiles.length > 0 && (
              <div style={{ marginTop: 8, fontSize: "0.9em", color: "#666" }}>
                선택된 파일: {selectedFiles.map(f => f.name).join(", ")}
              </div>
            )}
          </label>

          <div>
            <button type="submit">{editId ? "수정 완료" : "등록"}</button>
            {editId && (
              <button
                type="button"
                style={{ marginLeft: 8 }}
                onClick={() => {
                  setEditId(null);
                  setTitle("");
                  setContent("");
                  setSelectedFiles([]);
                  setHasFile(false);
                }}
              >
                취소
              </button>
            )}
          </div>
        </form>
      </section>

      <section
        style={{
          border: "1px solid #ddd",
          borderRadius: 12,
          padding: 16,
        }}
      >
        <h2 style={{ marginTop: 0 }}>공지 목록</h2>
        {loading && <div>불러오는 중…</div>}
        {!loading && list.length === 0 && <div>등록된 공지가 없습니다.</div>}
        {!loading && list.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th>ID</th>
                <th>분류</th>
                <th>제목</th>
                <th>생성일</th>
                <th>파일</th>
                <th>액션</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{row.type}</td>
                  <td>{row.title}</td>
                  <td>{row.date}</td>
                  <td>{row.hasFile ? "Y" : "N"}</td>
                  <td>
                    <button onClick={() => onEdit(row)}>수정</button>
                    <button onClick={() => onDelete(row.id)}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}